import React from 'react';
import { Box, ButtonV2, ColumnsV2, DialogV2, Flex, FormControl, InputV2, Spinner, SpacingStack, TextArea, TextV2, TooltipV2, CancelButton } from '@withjoy/joykit';
import { Divider } from '@apps/registry/common/components';
import { useTranslation } from '@shared/core/i18n';
import { ReactComponent as Camera } from '@assets/icons/camera2.svg';
import { useAddCustomGiftDialogController } from './AddCustomGiftDialog.controller';
import {
  EditPhotoButton,
  StyledButtonsContainer,
  StyledFlexPriceHeader,
  StyledLink,
  StyledPhoto,
  StyledPhotoText,
  StyledPriceQtyContainer,
  StyledStackPhoto,
} from './AddCustomGiftDialog.styles';
import { preventNonNumericalInput } from '@shared/utils/preventNonNumericalInput';
import { ReactComponent as Help } from '@assets/icons/help.svg';
import type { RegistryOrderList } from '@apps/registry/common/state/RegistryProducts';

export interface EditGiftDialogProps
  extends Readonly<{
    currency: string;
    eventId: string;
    showAddressReminderDialog?: () => void;
    showCurrencyDialog: () => void;
    orders?: RegistryOrderList;
    isOpen: boolean;
    onClose: () => void;
  }> {}

export const AddCustomGiftDialog: React.FC<EditGiftDialogProps> = ({ currency, eventId, showAddressReminderDialog, showCurrencyDialog, isOpen, onClose }) => {
  const { formik, handleTooltipClick, registryItemLoading, openFilepicker, updatedPhoto, handleConfirm, handleCancel } = useAddCustomGiftDialogController({
    eventId,
    showAddressReminderDialog,
    onClose,
    showCurrencyDialog,
  });

  const { errors } = formik || {};

  const { t, t2 } = useTranslation('sharedRegistry');
  const {
    addGiftTitle,
    linkField,
    titleField,
    priceField,
    qtyField,
    noteField,
    noteFieldPlaceholder,
    photoField,
    addGiftButton,
    cancelButton,
    changeCurrencyTitle,
    currencyLinkText,
    helpText,
  } = t2('giftDialog');

  const currencyBodyText = t(
    'giftDialog',
    'changeCurrencyBody',
  )({
    currencyLink: () => <StyledLink onClick={handleTooltipClick}>{currencyLinkText}</StyledLink>,
  });

  const errorLink = formik.touched.link && !!errors.link ? formik.errors.link : undefined;
  const errorTitle = formik.touched.title && !!errors.title ? formik.errors.title : undefined;
  return (
    <DialogV2
      id={'AddGiftDialog'}
      isOpen={isOpen}
      size="3xl"
    >
      <DialogV2.Header>
        <TextV2 typographyVariant="hed2">{addGiftTitle}</TextV2>
        <DialogV2.CloseButton onClick={onClose} />
      </DialogV2.Header>
      <DialogV2.Body marginTop={48}>
        <ColumnsV2>
          <ColumnsV2.Column>
            <StyledPhoto
              backgroundImage={updatedPhoto}
              backgroundColor={'mono2'}
              width={'auto'}
              height={328}
              marginBottom={'10px'}
              marginX="auto"
            >
              <div id="preview-photo" />
              <StyledStackPhoto spacing={5}>
                <EditPhotoButton
                  backgroundImage={updatedPhoto}
                  onClick={openFilepicker}
                >
                  {<Camera />}
                </EditPhotoButton>
                {!updatedPhoto ? (
                  <StyledPhotoText
                    typographyVariant="body1"
                    color="mono6"
                  >
                    {photoField}
                  </StyledPhotoText>
                ) : undefined}
              </StyledStackPhoto>
            </StyledPhoto>
          </ColumnsV2.Column>
          <ColumnsV2.Column marginTop={[24, 24, 0]}>
            <TextV2
              component="p"
              marginBottom={8}
            >
              {helpText}
            </TextV2>
            <form>
              <SpacingStack spacing={7}>
                <>
                  <FormControl
                    label={linkField}
                    isRequired
                    isInvalid={formik.touched.link && !!errors.link}
                    error={errorLink}
                  >
                    <InputV2 {...formik.getFieldProps('link')} />
                  </FormControl>
                  <FormControl
                    label={titleField}
                    isRequired
                    isInvalid={formik.touched.title && !!errors.title}
                    error={errorTitle}
                  >
                    <InputV2 {...formik.getFieldProps('title')} />
                  </FormControl>
                </>
                <StyledPriceQtyContainer>
                  <FormControl
                    width="100%"
                    isRequired
                    isInvalid={!!errors.price}
                    error={errors.price}
                    label={
                      <StyledFlexPriceHeader height={18}>
                        {`${priceField} (${currency})`}
                        <Flex>
                          <TooltipV2
                            content={
                              <Box>
                                <TextV2
                                  color="white"
                                  typographyVariant="label3"
                                >
                                  {changeCurrencyTitle}
                                </TextV2>
                                <br />
                                <TextV2
                                  color="white"
                                  typographyVariant="label2"
                                >
                                  {currencyBodyText}
                                </TextV2>
                              </Box>
                            }
                            placement={'topEnd'}
                          >
                            <TextV2>
                              <Help />
                            </TextV2>
                          </TooltipV2>
                        </Flex>
                      </StyledFlexPriceHeader>
                    }
                  >
                    <InputV2
                      {...formik.getFieldProps('price')}
                      min="0"
                      onKeyDown={preventNonNumericalInput}
                      isFullWidth
                    />
                  </FormControl>
                  <FormControl
                    width="100%"
                    label={qtyField}
                    isRequired
                    error={errors.qty}
                    isInvalid={!!errors.qty}
                  >
                    <InputV2
                      {...formik.getFieldProps('qty')}
                      type="number"
                      min="1"
                      onKeyDown={preventNonNumericalInput}
                      isFullWidth
                    />
                  </FormControl>
                </StyledPriceQtyContainer>
                <FormControl
                  marginTop={2}
                  label={noteField}
                  isRequired={false}
                  error={errors.note}
                >
                  <TextArea
                    {...formik.getFieldProps('note')}
                    placeholder={noteFieldPlaceholder}
                    resize={false}
                    name="note"
                  />
                </FormControl>
              </SpacingStack>
            </form>
          </ColumnsV2.Column>
        </ColumnsV2>
      </DialogV2.Body>
      <Divider margin="0px" />
      <DialogV2.Footer>
        <StyledButtonsContainer
          width={['100%', 'auto']}
          flexDirection={['column-reverse', 'row']}
        >
          <CancelButton
            disabled={registryItemLoading}
            onClick={handleCancel}
            width={['100%', '120px']}
          >
            {cancelButton}
          </CancelButton>
          <ButtonV2
            intent="primary"
            onClick={handleConfirm}
            disabled={registryItemLoading}
            type="submit"
            width={['100%', '120px']}
          >
            {registryItemLoading ? <Spinner /> : addGiftButton}
          </ButtonV2>
        </StyledButtonsContainer>
      </DialogV2.Footer>
    </DialogV2>
  );
};
